import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import CyberneticLoader from '@/components/CyberneticLoader';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  TrendingUp,
  DollarSign,
  Clock,
  CheckCircle2,
  XCircle,
  Package,
  Zap,
  Award,
} from 'lucide-react';

const COLORS = ['#224049', '#D4AF37', '#388E3C', '#F57C00', '#1976D2', '#8E44AD', '#D32F2F'];

const STATUS_COLORS = {
  CREATED: '#94a3b8',
  READY_FOR_PICKUP: '#F57C00',
  IN_CUSTODY: '#1976D2',
  EN_ROUTE: '#0EA5E9',
  ARRIVED: '#8E44AD',
  DELIVERED: '#388E3C',
  FAILED_DELIVERY: '#D32F2F',
  CANCELLED: '#64748b',
};

const Analytics = ({ user, onLogout }) => {
  const [days, setDays] = useState('30');
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [ordersByDay, setOrdersByDay] = useState([]);
  const [ordersByStatus, setOrdersByStatus] = useState([]);
  const [topDispensaries, setTopDispensaries] = useState([]);
  const [riderPerformance, setRiderPerformance] = useState([]);
  const [revenueByMode, setRevenueByMode] = useState([]);
  const [hourly, setHourly] = useState([]);

  const isAdminLike = user.role === 'admin' || user.role === 'support';

  useEffect(() => {
    fetchAnalytics();
  }, [days]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const requests = [
        axios.get(`/analytics/kpis?days=${days}`),
        axios.get(`/analytics/orders-by-day?days=${days}`),
        axios.get(`/analytics/orders-by-status?days=${days}`),
        axios.get(`/analytics/revenue-by-mode?days=${days}`),
        axios.get(`/analytics/hourly-distribution?days=${Math.min(parseInt(days), 30)}`),
      ];
      if (isAdminLike) {
        requests.push(axios.get(`/analytics/top-dispensaries?days=${days}&limit=10`));
        requests.push(axios.get(`/analytics/rider-performance?days=${days}&limit=15`));
      }
      const res = await Promise.all(requests);
      setKpis(res[0].data);
      setOrdersByDay(res[1].data);
      setOrdersByStatus(res[2].data);
      setRevenueByMode(res[3].data);
      setHourly(res[4].data);
      if (isAdminLike) {
        setTopDispensaries(res[5].data);
        setRiderPerformance(res[6].data);
      }
    } catch (err) {
      console.error('Analytics fetch error', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} title="Analítica">
        <div className="flex items-center justify-center h-96">
          <CyberneticLoader size={100} message="Cargando analítica..." />
        </div>
      </Layout>
    );
  }

  const kpiCards = [
    { label: 'Pedidos', value: kpis?.total_orders || 0, icon: Package, color: '#224049', bg: '#E8F0F1' },
    { label: 'Entregados', value: kpis?.delivered_orders || 0, icon: CheckCircle2, color: '#388E3C', bg: '#E8F5E9' },
    { label: 'Cancelados', value: kpis?.cancelled_orders || 0, icon: XCircle, color: '#D32F2F', bg: '#FFEBEE' },
    { label: 'Express', value: kpis?.express_orders || 0, icon: Zap, color: '#D4AF37', bg: '#FFF9C4' },
    { label: 'Ingresos', value: `$${(kpis?.total_revenue || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, icon: DollarSign, color: '#0F766E', bg: '#CCFBF1' },
    { label: 'Tiempo Prom.', value: `${kpis?.avg_delivery_time_min || 0} min`, icon: Clock, color: '#1976D2', bg: '#E3F2FD' },
    { label: 'On-Time', value: `${kpis?.on_time_rate || 0}%`, icon: TrendingUp, color: '#388E3C', bg: '#E8F5E9' },
    { label: 'Completion', value: `${kpis?.completion_rate || 0}%`, icon: Award, color: '#8E44AD', bg: '#F3E8FF' },
  ];

  return (
    <Layout user={user} onLogout={onLogout} title="Analítica Avanzada">
      <div className="space-y-6" data-testid="analytics-container">
        {/* Header + period selector */}
        <div
          className="card-premium p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          style={{ background: 'linear-gradient(135deg, #224049 0%, #2D5A6B 100%)' }}
        >
          <div>
            <h1 className="text-3xl font-bold text-white">Analítica Avanzada</h1>
            <p className="text-sm mt-1" style={{ color: '#D4AF37' }}>
              KPIs, tendencias y desempeño en tiempo real
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-white text-sm">Periodo:</span>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger
                className="w-40 bg-white"
                data-testid="analytics-period-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Últimos 7 días</SelectItem>
                <SelectItem value="14">Últimos 14 días</SelectItem>
                <SelectItem value="30">Últimos 30 días</SelectItem>
                <SelectItem value="60">Últimos 60 días</SelectItem>
                <SelectItem value="90">Últimos 90 días</SelectItem>
                <SelectItem value="365">Último año</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="analytics-kpi-grid">
          {kpiCards.map((kpi, i) => {
            const Icon = kpi.icon;
            return (
              <div
                key={i}
                className="metric-card"
                data-testid={`analytics-kpi-${kpi.label.toLowerCase().replace(/\s|\./g, '-')}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{kpi.label}</span>
                  <div className="p-2 rounded-full" style={{ backgroundColor: kpi.bg }}>
                    <Icon className="w-4 h-4" style={{ color: kpi.color }} />
                  </div>
                </div>
                <div className="text-2xl md:text-3xl font-bold" style={{ color: kpi.color }}>
                  {kpi.value}
                </div>
              </div>
            );
          })}
        </div>

        {/* Row: Orders by day (line) + Orders by status (pie) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="card-premium p-6 lg:col-span-2" data-testid="chart-orders-by-day">
            <h2 className="text-lg font-bold mb-4" style={{ color: '#224049' }}>
              Pedidos por día
            </h2>
            {ordersByDay.length === 0 ? (
              <div className="text-center py-16 text-gray-500 text-sm">Sin datos para este periodo</div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={ordersByDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="total" name="Total" stroke="#224049" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="delivered" name="Entregados" stroke="#388E3C" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="express" name="Express" stroke="#D4AF37" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="card-premium p-6" data-testid="chart-orders-by-status">
            <h2 className="text-lg font-bold mb-4" style={{ color: '#224049' }}>
              Distribución por Estado
            </h2>
            {ordersByStatus.length === 0 ? (
              <div className="text-center py-16 text-gray-500 text-sm">Sin datos</div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={ordersByStatus}
                    dataKey="count"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ status, percent }) => `${status} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                    fontSize={11}
                  >
                    {ordersByStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.status] || COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Row: Revenue by mode + Hourly distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card-premium p-6" data-testid="chart-revenue-by-mode">
            <h2 className="text-lg font-bold mb-4" style={{ color: '#224049' }}>
              Ingresos por modalidad
            </h2>
            {revenueByMode.length === 0 ? (
              <div className="text-center py-12 text-gray-500 text-sm">Sin datos</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={revenueByMode}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="mode" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, fontSize: 12 }}
                    formatter={(v, k) => (k === 'revenue' ? `$${v.toLocaleString('es-MX')}` : v)}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="orders" name="Pedidos" fill="#224049" />
                  <Bar dataKey="revenue" name="Ingresos ($)" fill="#D4AF37" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="card-premium p-6" data-testid="chart-hourly-distribution">
            <h2 className="text-lg font-bold mb-4" style={{ color: '#224049' }}>
              Distribución horaria (0-23 h)
            </h2>
            {hourly.length === 0 || hourly.every((h) => h.count === 0) ? (
              <div className="text-center py-12 text-gray-500 text-sm">Sin datos</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={hourly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="count" name="Pedidos" fill="#1976D2" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Admin-only sections */}
        {isAdminLike && (
          <>
            {/* Top dispensaries */}
            <div className="card-premium p-6" data-testid="table-top-dispensaries">
              <h2 className="text-lg font-bold mb-4" style={{ color: '#224049' }}>
                Top Dispensarios
              </h2>
              {topDispensaries.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">Sin datos</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b" style={{ backgroundColor: '#F5F1EA' }}>
                        <th className="text-left px-3 py-2 font-semibold" style={{ color: '#224049' }}>#</th>
                        <th className="text-left px-3 py-2 font-semibold" style={{ color: '#224049' }}>Dispensario</th>
                        <th className="text-right px-3 py-2 font-semibold" style={{ color: '#224049' }}>Pedidos</th>
                        <th className="text-right px-3 py-2 font-semibold" style={{ color: '#224049' }}>Entregados</th>
                        <th className="text-right px-3 py-2 font-semibold" style={{ color: '#224049' }}>Ingresos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topDispensaries.map((d, i) => (
                        <tr key={d.dispensary_id} className="border-b hover:bg-gray-50">
                          <td className="px-3 py-2 font-bold" style={{ color: '#D4AF37' }}>{i + 1}</td>
                          <td className="px-3 py-2">{d.dispensary_name}</td>
                          <td className="px-3 py-2 text-right">{d.orders}</td>
                          <td className="px-3 py-2 text-right">{d.delivered}</td>
                          <td className="px-3 py-2 text-right font-semibold">
                            ${d.revenue.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Rider performance */}
            <div className="card-premium p-6" data-testid="table-rider-performance">
              <h2 className="text-lg font-bold mb-4" style={{ color: '#224049' }}>
                Desempeño de Riders
              </h2>
              {riderPerformance.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">Sin datos</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b" style={{ backgroundColor: '#F5F1EA' }}>
                        <th className="text-left px-3 py-2 font-semibold" style={{ color: '#224049' }}>#</th>
                        <th className="text-left px-3 py-2 font-semibold" style={{ color: '#224049' }}>Rider</th>
                        <th className="text-right px-3 py-2 font-semibold" style={{ color: '#224049' }}>Pedidos</th>
                        <th className="text-right px-3 py-2 font-semibold" style={{ color: '#224049' }}>Entregados</th>
                        <th className="text-right px-3 py-2 font-semibold" style={{ color: '#224049' }}>Cancelados</th>
                        <th className="text-right px-3 py-2 font-semibold" style={{ color: '#224049' }}>On-Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {riderPerformance.map((r, i) => (
                        <tr key={r.rider_id} className="border-b hover:bg-gray-50">
                          <td className="px-3 py-2 font-bold" style={{ color: '#D4AF37' }}>{i + 1}</td>
                          <td className="px-3 py-2">
                            <div className="font-medium">{r.rider_name}</div>
                            <div className="text-xs text-gray-500">{r.rider_email}</div>
                          </td>
                          <td className="px-3 py-2 text-right">{r.orders}</td>
                          <td className="px-3 py-2 text-right text-green-700 font-semibold">{r.delivered}</td>
                          <td className="px-3 py-2 text-right text-red-600">{r.cancelled}</td>
                          <td className="px-3 py-2 text-right">
                            <span
                              className="px-2 py-1 rounded font-semibold text-xs"
                              style={{
                                backgroundColor: r.on_time_rate >= 90 ? '#E8F5E9' : r.on_time_rate >= 70 ? '#FFF3E0' : '#FFEBEE',
                                color: r.on_time_rate >= 90 ? '#388E3C' : r.on_time_rate >= 70 ? '#F57C00' : '#D32F2F',
                              }}
                            >
                              {r.on_time_rate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Analytics;
